import React, { useState, useEffect } from 'react';
import { Translate as T } from 'react-redux-i18n';
import { formValueSelector, change } from 'redux-form';
import { connect, useDispatch } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import { trackAnalyticsEvent } from '../../../actions';
import { ANALYTICS_EVENTS } from '../../../lib/analytics';
import UploadButton from '../../fields/UploadButton';
import { VERIFICATION_FORM_NAME } from '../../../constants';
import SimplePanel from '../../Panels/SimplePanel';
import {
  DocumentListSection,
  DocumentUploadHeader,
  GenerateDateComponents,
  UploadCircleWrapper,
} from './DocumentVerificationComponents';
import Logo from '../../Logo';
import DocumentInfoModal from './DocumentInfoModal';
import CandidateExplanationModal from './CandidateExplanationModal';
import {
  extractDocumentRequirements,
  getDateYear,
  getTranslation,
  groupedDocuments,
  splitDocuments,
  updateEnabledDocuments,
  uploadedFilesUpdate,
  onCancel,
  onSubmit,
  createOnRemoveByUploadId,
} from './utils/formatter';
import {
  DateObject,
  DocumentAccessProps,
  UploadSectionProps,
} from './utils/interfaces';
import { dateObject } from './utils/constants';

import './EmploymentMultidocPanel.scss';

const UploadSection: React.FC<UploadSectionProps> = ({
  documents,
  model,
  policy,
  updateDocuments,
  getDocuments,
  currentDocumentsRequired,
  setCurrentDocumentsRequired,
  setDocuments,
  endDate,
  documentTypes,
  additionalDocuments,
  onRemoveByUploadId,
}) => {
  const dispatch = useDispatch();
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [candidateExplanation, setCandidateExplanation] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const {
    documentsRequired,
    documentsProvided,
    remainingDocuments,
    optionalDocuments,
  } = groupedDocuments(currentDocumentsRequired);
  const exceptionId = optionalDocuments[0]?.id ?? '';
  const additionalDocumentsWithException = additionalDocuments.map(file => ({
    ...file,
    exceptionId,
  }));
  const handleUploadSuccess = (res: { filesUploaded: File[] }) => {
    setUploadedFiles(res.filesUploaded);
    setDocuments([...documents, ...res.filesUploaded]);
    updateDocuments(model, undefined);
    res.filesUploaded.length > 0 && setIsDocumentModalOpen(true);
  };

  const handleModalUploadSuccess = (res: { filesUploaded: any[] }) => {
    const dateObjects: DateObject[] = [];
    const filesWithExceptionId = res.filesUploaded.map(file => {
      const dateObj = {
        ...dateObject(exceptionId, file.uploadId),
        exceptionId,
      };
      dateObjects.push(dateObj);

      return {
        ...file,
        id: file?.uploadId,
        exceptionId,
      };
    });
    setDocuments([...documents, ...filesWithExceptionId]);
    updateDocuments(model, undefined);
    handleUploadedFilesUpdate(dateObjects, filesWithExceptionId);
  };
  const analyticsPayload = {
    documentsRequired: documentsRequired.length,
    documentsProvided: documentsProvided.length,
    policy,
  };
  const handleSubmitExplanation = async () => {
    const updated = {
      ...(getDocuments(exceptionId) || []),
      candidate_explanation: candidateExplanation,
    };
    await updateDocuments(exceptionId, updated);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_EXPLANATION_SUBMITTED,
        analyticsPayload,
      ),
    );
  };

  const handleUploadedFilesUpdate = (stateYears: any, uploadedDocs: any) => {
    return uploadedFilesUpdate(
      uploadedDocs,
      stateYears,
      updateDocuments,
      getDocuments,
    );
  };

  const handleDocumentInfoCancel = () => {
    onCancel(setIsDocumentModalOpen, setDocuments, uploadedFiles);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_DOCUMENTS_CLOSED,
        analyticsPayload,
      ),
    );
  };

  const handleDocumentInfoSubmit = () => {
    onSubmit(setIsDocumentModalOpen);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_DOCUMENTS_LABELED,
        analyticsPayload,
      ),
    );
  };

  useEffect(() => {
    const uploadedIds = documents.map(({ uploadId }) => uploadId);
    const updated = updateEnabledDocuments(
      currentDocumentsRequired,
      uploadedIds,
    );
    setCurrentDocumentsRequired(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_PAGE_VIEWED,
        analyticsPayload,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const requiredTypes = documentTypes
    .filter(doc => doc.requiredForVerification)
    .map(doc => getTranslation(doc.type))
    .join(', ');

  return (
    <SimplePanel>
      <M.Grid>
        <M.GridRow>
          <M.GridCol md={12}>
            <h1>
              <T value='buttons.uploadDocument' className='text-panel' />
            </h1>
            <hr />
            <T
              value='components.Verification.EmploymentMultidocPanel.upload'
              documentTypes={requiredTypes}
              className='text-panel'
              dangerousHTML
            />
          </M.GridCol>
        </M.GridRow>
        <br />
        <br />
        <M.GridRow>
          <M.GridCol lg={8} className='no-padding text-left'>
            <h2>
              <T
                value='components.employment.requestedDocuments'
                className='text-panel'
              />
            </h2>
            <GenerateDateComponents
              documentsRequired={documentsRequired}
              policy={policy}
              endDate={endDate}
            />
          </M.GridCol>
          <M.GridCol lg={8} className='no-padding'>
            <SimplePanel>
              <div className='content-center'>
                <UploadCircleWrapper
                  progress={documentsProvided.length / documentsRequired.length}
                />
                <T
                  value='components.employment.documentProgress'
                  progress={documentsProvided.length}
                  total={documentsRequired.length}
                  className='text-panel'
                />
                <UploadButton
                  kind='secondary'
                  name={model}
                  hideIcon
                  buttonText='buttons.uploadDocument'
                  buttonClass=''
                  context='multi-documents'
                  fileOptions={{ maxFiles: documentsRequired.length + 10 }}
                  handleUploadSuccess={handleUploadSuccess}
                  shouldSubmit={
                    documentsRequired.length <= documentsProvided.length ||
                    (candidateExplanation.length > 0 && !isExplanationModalOpen)
                  }
                />
                {isDocumentModalOpen && uploadedFiles && (
                  <DocumentInfoModal
                    file={uploadedFiles}
                    documentsRequired={currentDocumentsRequired}
                    onDocumentsRequiredChange={setCurrentDocumentsRequired}
                    handleUploadedFilesUpdate={handleUploadedFilesUpdate}
                    onCancel={handleDocumentInfoCancel}
                    onSubmit={handleDocumentInfoSubmit}
                    documentTypes={documentTypes}
                    uploadedFiles={uploadedFiles}
                  />
                )}
                {remainingDocuments.length > 0 && (
                  <M.Button
                    onClick={() => setIsExplanationModalOpen(true)}
                    className='unset-pointer'
                  >
                    <T
                      value='components.employment.missingDocuments'
                      className='document-upload-header'
                    />
                  </M.Button>
                )}
                {isExplanationModalOpen && (
                  <CandidateExplanationModal
                    model={model}
                    documentsRequired={documentsRequired}
                    policy={policy}
                    candidateExplanation={candidateExplanation}
                    setCandidateExplanation={setCandidateExplanation}
                    setIsModalOpen={setIsExplanationModalOpen}
                    handleSubmitExplanation={handleSubmitExplanation}
                    endDate={endDate}
                    additionalDocuments={additionalDocumentsWithException}
                    onRemoveByUploadId={onRemoveByUploadId}
                    handleModalUploadSuccess={handleModalUploadSuccess}
                  />
                )}
              </div>
            </SimplePanel>
          </M.GridCol>
        </M.GridRow>
      </M.Grid>
    </SimplePanel>
  );
};

const EmploymentMultidocPanel: React.FC<DocumentAccessProps> = ({
  updateDocuments,
  getDocuments,
  model = '',
  verification = {},
}) => {
  const [documents, setDocuments] = useState<DateObject[]>([]);
  const onRemoveByUploadId = createOnRemoveByUploadId(
    setDocuments,
    getDocuments,
    updateDocuments,
  );

  const {
    company,
    companyLogoUri,
    documentsRequired,
    endDateFormatted,
    hiringPolicy,
    position,
    startDateFormatted,
    type,
  } = extractDocumentRequirements(verification);

  const [currentDocumentsRequired, setCurrentDocumentsRequired] =
    useState(documentsRequired);
  const [requestedDocuments, additionalDocuments] = splitDocuments(
    documents,
    currentDocumentsRequired,
  );

  return (
    <div>
      <SimplePanel>
        {companyLogoUri && <Logo logoUri={companyLogoUri} />}
        <DocumentUploadHeader
          position={position}
          company={company}
          startDate={startDateFormatted}
          endDate={endDateFormatted}
        />
        <M.Grid>
          <M.GridRow>
            <M.GridCol md={12}>
              <T
                value='components.Verification.EmploymentMultidocPanel.text'
                className='text-panel'
                employerName={company}
                dangerousHTML
              />
            </M.GridCol>
          </M.GridRow>
        </M.Grid>
      </SimplePanel>
      <UploadSection
        documents={documents}
        setDocuments={setDocuments}
        model={model}
        policy={hiringPolicy}
        getDocuments={getDocuments}
        updateDocuments={updateDocuments}
        currentDocumentsRequired={currentDocumentsRequired}
        setCurrentDocumentsRequired={setCurrentDocumentsRequired}
        endDate={getDateYear(endDateFormatted)}
        documentTypes={type}
        additionalDocuments={additionalDocuments}
        onRemoveByUploadId={onRemoveByUploadId}
      />
      <DocumentListSection
        documents={requestedDocuments}
        additionalDocuments={additionalDocuments}
        onRemoveByUploadId={onRemoveByUploadId}
      />
    </div>
  );
};

const selector = formValueSelector(VERIFICATION_FORM_NAME);

export const mapStateToProps = (state: any) => ({
  getDocuments: (field: string) => selector(state, field),
});

const reduxStateConnector = connect(mapStateToProps, {
  updateDocuments: (field: string, documents: any) =>
    change(VERIFICATION_FORM_NAME, field, documents),
})(EmploymentMultidocPanel);

export default i18n.renderTranslation()(reduxStateConnector);
